import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { getReactionsSummary, getReactionsQueryInfo } from '../utils/data.ts';
import { getBooleanLocationTypes } from '../utils/reactions-utils.ts';
import type { ReactionsContentQuery_content_nodes_contentReactionsSummary_reactionsSummaryForEmoji as ReactionsSummaryForEmoji } from '../graphql/__types__/ReactionsContentQuery.ts';
import type {
	ReactionLocation,
	Reaction,
	ReactionsData,
	QueryData,
	QueryVariables,
	ReactedUsersMap,
} from '../reactionTypes.ts';

type ReactionsDataValue = {
	reactions: Reaction[];
	totalCount: number;
	ari: string;
	containerAri: string;
};

type ReactionsDataReturnType = {
	data: ReactionsDataValue;
	loading?: boolean;
	error?: ApolloErrorInterop;
};

type ReactionsDataFnProps = (input: {
	location: ReactionLocation;
	contentId: string;
	containerId: string;
	reactedUsersMap: ReactedUsersMap;
	useNetworkOnly: boolean;
	inheritedReactionsData?: ReactionsData;
	limit?: number;
	sort?: 'asc' | 'desc';
}) => ReactionsDataReturnType;

export const useReactionsData: ReactionsDataFnProps = ({
	location,
	contentId,
	containerId,
	reactedUsersMap,
	useNetworkOnly,
	inheritedReactionsData,
	limit,
	sort,
}) => {
	const { isComment } = getBooleanLocationTypes(location);
	const isLivePage = useIsCurrentPageLive();
	const isClassicEditPage = useIsEditorPage();

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isLivePageSSRMode = isLivePage && Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, containerId);

	const defaultFetchPolicy = expVal(
		'confluence_fe_use_reactions_data_cache_first',
		'isEnabled',
		false,
	)
		? 'cache-first'
		: 'cache-and-network';
	const { data, loading, error } = useQuery<QueryData, QueryVariables>(query, {
		variables: queryVariables,
		fetchPolicy: isComment && useNetworkOnly ? 'network-only' : defaultFetchPolicy,
		skip: inheritedReactionsData !== undefined, // null is a valid case meaning an error ocurred higher up
	});

	return useMemo(() => {
		// Sometimes we want to show preloaded data during loading instead of empty state
		const showPreloadedDataDuringLoading =
			isLivePageSSRMode || // Always show preloaded data during SSR
			((isLivePage || isClassicEditPage) && fg('confluence_perf_reactions_load_livepage')) || // Show preloaded data for live pages when flag is on
			window.__SSR_REACT_STREAMING__;

		const shouldShowEmptyState =
			(loading && !showPreloadedDataDuringLoading) || // Show empty while loading unless we want to show cached data
			error ||
			(!data && !inheritedReactionsData);

		if (shouldShowEmptyState) {
			return {
				data: {
					reactions: [],
					totalCount: 0,
					ari: '',
					containerAri: '',
				},
				loading,
				error,
			};
		}

		const reactionsSummary = inheritedReactionsData || getReactionsSummary(data);

		const totalCount = reactionsSummary?.reactionsCount || 0;
		const containerAri = reactionsSummary?.containerAri || '';
		const ari = reactionsSummary?.ari || '';
		const reactionNodes = reactionsSummary?.reactionsSummaryForEmoji || [];

		// We need to filter out all the "null" reactions
		const filteredNodes = reactionNodes?.filter(
			(reaction) => reaction !== null,
		) as ReactionsSummaryForEmoji[];

		// Transform reaction nodes to the correct shape for the AkReactions component
		let shapedReactions = filteredNodes.map(({ emojiId, count, reacted }) => {
			const users = reactedUsersMap[emojiId] || [];

			return {
				ari,
				containerAri,
				emojiId,
				count,
				reacted,
				users,
			};
		});

		if (sort) {
			shapedReactions = shapedReactions.sort((a, b) =>
				sort === 'asc' ? a.count - b.count : b.count - a.count,
			);
		}

		if (limit) {
			shapedReactions = shapedReactions.slice(0, limit);
		}

		return {
			data: {
				totalCount,
				reactions: shapedReactions,
				ari,
				containerAri,
			},
		};
	}, [
		data,
		inheritedReactionsData,
		reactedUsersMap,
		loading,
		error,
		sort,
		limit,
		isLivePageSSRMode,
		isLivePage,
		isClassicEditPage,
	]);
};
